import React, { useRef } from 'react';
import { getNextTabIndex, NAVIGATION_KEYS } from '../utils/rovingTabIndex';

/**
 * Accessible tablist implementing the real WAI-ARIA keyboard pattern (Step
 * 21) — replaces the click-only `role="tablist"` markup used across the app
 * before this step (Stock Analysis, Recommendations, Anomalies, Demand
 * Sensing, Notifications, Settings), which had the ARIA roles but none of
 * the keyboard behavior those roles promise to assistive-technology users.
 *
 * Roving tabindex: only the active tab is in the normal Tab order
 * (tabIndex=0); Tab itself moves focus out of the tablist as a single stop.
 * Arrow keys move focus AND selection between tabs; Home/End jump to the
 * first/last. The index-transition logic (rovingTabIndex.js) is unit-tested
 * independently of this DOM-wiring component.
 */
export default function TabList({ tabs, activeKey, onChange, ariaLabel }) {
  const buttonRefs = useRef([]);
  const activeIndex = Math.max(0, tabs.findIndex((t) => t.key === activeKey));

  const handleKeyDown = (e) => {
    if (!NAVIGATION_KEYS.has(e.key)) return;
    e.preventDefault(); // stop arrow keys from scrolling the page
    const nextIndex = getNextTabIndex(activeIndex, e.key, tabs.length);
    onChange(tabs[nextIndex].key);
    buttonRefs.current[nextIndex]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      style={{ display: 'flex', gap: 8, marginBottom: 'var(--space-4)', flexWrap: 'wrap' }}
    >
      {tabs.map((tab, i) => (
        <button
          key={tab.key}
          ref={(el) => { buttonRefs.current[i] = el; }}
          type="button"
          role="tab"
          aria-selected={activeKey === tab.key}
          tabIndex={activeKey === tab.key ? 0 : -1}
          onClick={() => onChange(tab.key)}
          className={`tab-btn${activeKey === tab.key ? ' tab-btn-active' : ''}`}
          style={{
            padding: '8px 14px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            background: activeKey === tab.key ? 'var(--color-accent)' : 'var(--color-surface)',
            color: activeKey === tab.key ? '#fff' : 'var(--color-text-primary)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
